/**
 * Phase 140B.2 Sprint 4 #2 — Story-of-the-Match Tab
 * -----------------------------------------------------------------
 * Embedded inside `LivexReplayPlayer.jsx` next to the Chapters list.
 * Renders the top-5 ranked clips for a stream as a cinematic auto-
 * advancing playlist — when the active clip's <video> fires `onEnded`,
 * the next clip in the queue auto-plays. Parents and coaches get a
 * one-tap "Story" view that strings together the most signal-rich
 * moments of a match without any manual editing.
 *
 * Reads:
 *   GET /api/streamx/story-of-match/{stream_id}   (public, no auth)
 *
 * Response shape:
 *   { stream_id, entries: [<clip>...], count }
 *   each clip: { id, title, mp4_url, thumbnail_url, duration_seconds,
 *                trigger_type, confidence, status, ... }
 *
 * Visual doctrine: obsidian (#0a0a0f) + Out-Play orange accent (#FF5A1F),
 * matching the surrounding `LivexReplayPlayer.jsx` aesthetic.
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import {
  Film, Play, Pause, AlertTriangle, Loader2, Sparkles, RotateCcw,
  SkipForward, SkipBack, Goal, Star, TrendingUp, Users, Clock,
} from 'lucide-react';
import api from '@/lib/api';

const TRIGGER_META = {
  manual:         { label: 'Manual', Icon: Star,       color: '#fbbf24', bg: 'rgba(251,191,36,0.18)', border: 'rgba(251,191,36,0.45)' },
  score_change:   { label: 'Goal',   Icon: Goal,       color: '#34d399', bg: 'rgba(52,211,153,0.18)', border: 'rgba(52,211,153,0.45)' },
  reaction_spike: { label: 'Crowd',  Icon: TrendingUp, color: '#fb923c', bg: 'rgba(251,146,60,0.18)', border: 'rgba(251,146,60,0.45)' },
  viewer_spike:   { label: 'Viewers', Icon: Users,     color: '#a78bfa', bg: 'rgba(167,139,250,0.18)', border: 'rgba(167,139,250,0.45)' },
};
const triggerMeta = (t) => TRIGGER_META[t] || { label: t || 'Moment', Icon: Sparkles, color: '#94A3B8', bg: 'rgba(148,163,184,0.18)', border: 'rgba(148,163,184,0.45)' };

function fmtDuration(sec) {
  const s = Math.max(0, Math.round(Number(sec || 0)));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${String(s % 60).padStart(2, '0')}s`;
}

export default function StoryOfTheMatchTab({ streamId }) {
  const videoRef = useRef(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [autoAdvance, setAutoAdvance] = useState(true);
  const [paused, setPaused] = useState(false);

  // ---- fetch the ranked top-5 once per streamId -------------------
  // Pattern note (matches LivexHighlights.jsx): we keep all setState
  // calls *inside* the async IIFE so the `react-hooks/set-state-in-effect`
  // rule doesn't flag the effect body. Default useState values cover
  // the "no streamId / not yet fetched" state.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!streamId) {
        if (!cancelled) {
          setEntries([]);
          setError('');
          setLoading(false);
        }
        return;
      }
      try {
        const res = await api.get(`/streamx/story-of-match/${streamId}`);
        if (cancelled) return;
        // Defensive: only keep clips that actually have a playable MP4.
        // The endpoint already filters to status='ready', but a clip can
        // technically be "ready" without an mp4_url if storage upload
        // raced — we surface a clean empty state in that case.
        const ready = (res.data?.entries || []).filter((c) => c && c.mp4_url);
        setEntries(ready);
        setActiveIndex(0);
        setError('');
      } catch (e) {
        if (cancelled) return;
        // 404 on the upstream stream lookup means the replay isn't
        // linked to a stream record (legacy data). Treat as "no story
        // yet" instead of a hard error so the UI degrades gracefully.
        if (e?.response?.status === 404) {
          setEntries([]);
          setError('');
        } else {
          setError(e?.response?.data?.detail || 'Could not load Story of the Match.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [streamId]);

  const activeClip = entries[activeIndex] || null;

  // ---- auto-advance on clip end -----------------------------------
  const handleEnded = useCallback(() => {
    if (!autoAdvance) return;
    setActiveIndex((idx) => (idx + 1 < entries.length ? idx + 1 : idx));
  }, [autoAdvance, entries.length]);

  // ---- load new src + autoplay when index changes -----------------
  useEffect(() => {
    if (!videoRef.current || !activeClip) return;
    try {
      videoRef.current.load();
      // Autoplay only after the user has interacted (i.e., after the
      // first clip). Browsers block muted-less autoplay on first paint
      // for non-user-gestures; we accept the silent reject here and
      // let the operator hit Play manually.
      const playPromise = videoRef.current.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => { setPaused(true); });
      }
    } catch { /* noop */ }
  }, [activeIndex, activeClip]);

  const goPrev = () => setActiveIndex((idx) => Math.max(0, idx - 1));
  const goNext = () => setActiveIndex((idx) => Math.min(entries.length - 1, idx + 1));
  const restart = () => {
    setActiveIndex(0);
    setAutoAdvance(true);
    setPaused(false);
  };
  const togglePlayback = () => {
    if (!videoRef.current) return;
    if (videoRef.current.paused) {
      videoRef.current.play().catch(() => {});
      setPaused(false);
    } else {
      videoRef.current.pause();
      setPaused(true);
    }
  };

  // -------------------- render branches ----------------------------
  if (loading) {
    return (
      <section
        data-testid="livex-story-loading"
        className="bg-[#07111F] border-2 border-[#22314F] rounded-2xl p-8 text-center"
      >
        <Loader2 className="h-6 w-6 mx-auto animate-spin text-[#FF5A1F]" />
        <div className="mt-3 text-xs font-bold uppercase tracking-[0.18em] text-white/55">
          Assembling Story of the Match…
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section
        data-testid="livex-story-error"
        className="bg-[#07111F] border-2 border-[#22314F] rounded-2xl p-6 text-center"
      >
        <AlertTriangle className="h-8 w-8 mx-auto text-rose-400 mb-2" />
        <h3 className="text-sm font-black text-white">Could not load story</h3>
        <p className="text-[12px] text-[#94A3B8] mt-1 max-w-md mx-auto">{error}</p>
      </section>
    );
  }

  if (entries.length === 0) {
    return (
      <section
        data-testid="livex-story-empty"
        className="bg-[#07111F] border-2 border-[#22314F] rounded-2xl p-8 text-center"
      >
        <Sparkles className="h-8 w-8 mx-auto text-[#94A3B8] mb-2" />
        <h3 className="text-sm font-black text-white">No premium reels yet</h3>
        <p className="text-[12px] text-[#94A3B8] mt-1 max-w-md mx-auto">
          The Story compiles the top-5 most signal-rich moments after AI extraction finishes.
          Mark live highlights during your next broadcast with the ⭐ button, or let StreamX
          auto-detect goals, crowd spikes and viewer surges.
        </p>
      </section>
    );
  }

  const tMeta = activeClip ? triggerMeta(activeClip.trigger_type) : null;
  const TIcon = tMeta?.Icon;

  return (
    <section
      data-testid="livex-story-tab"
      className="bg-[#07111F] border-2 border-[#22314F] rounded-2xl p-4 sm:p-5"
    >
      {/* Header strip ------------------------------------------------ */}
      <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <Sparkles className="h-4 w-4 text-[#FF5A1F] flex-shrink-0" />
          <h3 className="text-sm font-black text-white tracking-tight truncate">
            Story of the Match · top {entries.length} {entries.length === 1 ? 'moment' : 'moments'}
          </h3>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => setAutoAdvance((v) => !v)}
            data-testid="livex-story-autoplay-toggle"
            className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-md border-2 transition-all ${
              autoAdvance ? 'border-[#FF5A1F] text-white bg-[#FF5A1F]' : 'border-[#22314F] text-[#94A3B8] hover:border-[#FF5A1F]'
            }`}
          >
            Auto · {autoAdvance ? 'ON' : 'OFF'}
          </button>
          <button
            type="button"
            onClick={restart}
            data-testid="livex-story-restart"
            className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-md border-2 border-[#22314F] text-white hover:border-[#FF5A1F] transition-all"
          >
            <RotateCcw className="h-2.5 w-2.5" /> Restart
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-5">
        {/* Cinematic player ------------------------------------------ */}
        <div className="lg:col-span-2 space-y-3">
          <div
            className="relative w-full rounded-xl overflow-hidden border-2 border-[#22314F]"
            style={{ aspectRatio: '16/9', background: '#050a14' }}
            data-testid="livex-story-player-area"
          >
            <video
              key={activeClip?.id || 'story-player'}
              ref={videoRef}
              src={activeClip?.mp4_url}
              poster={activeClip?.thumbnail_url || undefined}
              onEnded={handleEnded}
              onPause={() => setPaused(true)}
              onPlay={() => setPaused(false)}
              controls
              playsInline
              className="absolute inset-0 w-full h-full bg-black"
              data-testid="livex-story-video"
            />
            <div
              className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[10px] font-black uppercase tracking-[0.16em]"
              style={{
                background: 'rgba(0,0,0,0.55)',
                color: 'white',
                border: '1px solid rgba(255,255,255,0.12)',
                backdropFilter: 'blur(8px)',
              }}
              data-testid="livex-story-segment-pill"
            >
              <Film className="h-2.5 w-2.5 text-[#FF5A1F]" /> Segment {activeIndex + 1} / {entries.length}
            </div>
          </div>

          {/* Active clip meta + transport controls --------------------- */}
          {activeClip && (
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  {tMeta && (
                    <span
                      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider flex-shrink-0"
                      style={{ background: tMeta.bg, color: tMeta.color, border: `1px solid ${tMeta.border}` }}
                      data-testid="livex-story-trigger-chip"
                    >
                      <TIcon className="h-2.5 w-2.5" /> {tMeta.label}
                    </span>
                  )}
                  <h4 className="text-sm font-black text-white truncate" data-testid="livex-story-title">
                    {activeClip.title || 'Match Moment'}
                  </h4>
                </div>
                <div className="mt-1 text-[10px] font-bold text-[#94A3B8] tabular-nums flex items-center gap-1.5">
                  <Clock className="h-2.5 w-2.5" /> {fmtDuration(activeClip.duration_seconds)}
                </div>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <button
                  type="button"
                  onClick={goPrev}
                  disabled={activeIndex === 0}
                  data-testid="livex-story-prev"
                  className="h-8 w-8 grid place-items-center rounded-lg border-2 border-[#22314F] text-white hover:border-[#FF5A1F] transition-all disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:border-[#22314F]"
                  title="Previous moment"
                >
                  <SkipBack className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={togglePlayback}
                  data-testid="livex-story-play-pause"
                  className="h-8 w-8 grid place-items-center rounded-lg border-2 border-[#FF5A1F] text-white transition-all hover:-translate-y-0.5"
                  style={{ background: 'linear-gradient(135deg, #dc2626, #f97316)' }}
                  title={paused ? 'Resume' : 'Pause'}
                >
                  {paused ? <Play className="h-3.5 w-3.5 fill-current" /> : <Pause className="h-3.5 w-3.5 fill-current" />}
                </button>
                <button
                  type="button"
                  onClick={goNext}
                  disabled={activeIndex >= entries.length - 1}
                  data-testid="livex-story-next"
                  className="h-8 w-8 grid place-items-center rounded-lg border-2 border-[#22314F] text-white hover:border-[#FF5A1F] transition-all disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:border-[#22314F]"
                  title="Next moment"
                >
                  <SkipForward className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Playlist sidebar ----------------------------------------- */}
        <div
          className="space-y-2 max-h-[480px] overflow-y-auto pr-1"
          data-testid="livex-story-playlist"
        >
          <div className="text-[10px] font-black uppercase tracking-[0.16em] text-[#94A3B8] sticky top-0 bg-[#07111F] pb-2 z-10">
            Match Timeline
          </div>
          {entries.map((clip, idx) => {
            const isActive = idx === activeIndex;
            const m = triggerMeta(clip.trigger_type);
            const M = m.Icon;
            return (
              <button
                key={clip.id || idx}
                type="button"
                onClick={() => setActiveIndex(idx)}
                data-testid={`livex-story-playlist-item-${idx}`}
                className={`w-full text-left p-2.5 rounded-xl border-2 transition-all flex items-start gap-2.5 ${
                  isActive
                    ? 'border-[#FF5A1F] bg-[#1a0e10]'
                    : 'border-[#22314F] bg-[#050a14] hover:border-[#FF5A1F]'
                }`}
              >
                <div
                  className="flex-shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-lg"
                  style={{ background: m.bg, border: `1px solid ${m.border}` }}
                >
                  <M className="h-3.5 w-3.5" style={{ color: m.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-black text-white truncate">
                    {clip.title || `Moment ${idx + 1}`}
                  </div>
                  <div className="text-[10px] font-bold text-[#94A3B8] mt-0.5 tabular-nums">
                    {m.label} · {fmtDuration(clip.duration_seconds)}
                  </div>
                </div>
                <div className="flex-shrink-0 self-center">
                  <span
                    className={`text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded ${
                      isActive ? 'bg-[#FF5A1F] text-white' : 'bg-[#22314F]/40 text-[#94A3B8]'
                    }`}
                    data-testid={`livex-story-playlist-item-${idx}-index`}
                  >
                    {String(idx + 1).padStart(2, '0')}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}
